import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaGavel, FaClock, FaHandPaper, FaHistory, FaSync, FaSearch, FaChevronLeft, FaChevronRight, FaDatabase, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useAuctionList } from '../../hooks/useAuctionList';
import AuctionCard from '../../components/Auction/AuctionCard';
import { activityTracker } from '../../services/activityTracker';
import mockDataService from '../../services/mockDataService';
import { PageContainer, PageHeader, Title, Subtitle, TabBar, TabButton, ContentArea, FilterBar, FilterSelect, SearchInput, RefreshButton, AuctionGrid, PaginationContainer, PaginationButton, PaginationInfo, EmptyState, LoadingSpinner, ErrorState, ResultsHeader, ResultsCount, } from './AuctionHouse.styles';
const tabs = [
    { id: 'active', label: 'Live Auctions', icon: _jsx(FaGavel, {}) },
    { id: 'upcoming', label: 'Upcoming', icon: _jsx(FaClock, {}) },
    { id: 'my-bids', label: 'My Bids', icon: _jsx(FaHandPaper, {}) },
    { id: 'history', label: 'History', icon: _jsx(FaHistory, {}) },
];
const SHAPES = [
    'All Shapes',
    'Round',
    'Princess',
    'Cushion',
    'Oval',
    'Emerald',
    'Pear',
    'Marquise',
    'Radiant',
    'Asscher',
    'Heart',
];
const SORT_OPTIONS = [
    { value: 'ending-soon', label: 'Ending Soon' },
    { value: 'newly-listed', label: 'Newly Listed' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'most-bids', label: 'Most Bids' },
    { value: 'carat-high', label: 'Carat: High to Low' },
];
const AuctionHouse = () => {
    const [activeTab, setActiveTab] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');
    const [shapeFilter, setShapeFilter] = useState('All Shapes');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sortBy, setSortBy] = useState('ending-soon');
    const [isMockMode, setIsMockMode] = useState(mockDataService.isMockMode());
    const navigate = useNavigate();
    const handleToggleMockMode = () => {
        const newMode = mockDataService.toggleMockMode();
        setIsMockMode(newMode);
        window.location.reload();
    };
    const { auctions, pagination, filters, isLoading, error, changeFilters, changePage, refresh, } = useAuctionList();
    // Track page view on mount
    useEffect(() => {
        activityTracker.trackPageView('/auctions', 'Auction House');
    }, []);
    // Map tab to status filter
    useEffect(() => {
        const statusMap = {
            'active': 'active',
            'upcoming': 'scheduled',
            'my-bids': 'active',
            'history': 'closed',
        };
        changeFilters({ status: statusMap[activeTab] });
    }, [activeTab, changeFilters]);
    // Apply type filter
    useEffect(() => {
        changeFilters({ type: typeFilter });
    }, [typeFilter, changeFilters]);
    // Handle tab change with tracking
    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        activityTracker.trackAction('auction-tab-change', `Switched to ${tab} tab`, { tab });
    }, []);
    // Handle search
    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
        changeFilters({ search: e.target.value });
    }, [changeFilters]);
    // Handle card click
    const handleAuctionClick = useCallback((auctionId) => {
        activityTracker.trackAction('auction-view', `Viewed auction ${auctionId}`, { auctionId });
        navigate(`/auctions/${auctionId}`);
    }, [navigate]);
    // Filtered auctions (client-side shape filter on top of server results)
    const filteredAuctions = useMemo(() => {
        let result = auctions;
        if (shapeFilter !== 'All Shapes') {
            result = result.filter(a => a.diamondSnapshot.shape.toLowerCase() === shapeFilter.toLowerCase());
        }
        return result;
    }, [auctions, shapeFilter]);
    // Pagination helpers
    const { page, totalPages, total } = pagination;
    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }, [page, totalPages]);
    // Empty state messages per tab
    const emptyMessages = {
        'active': { title: 'No Live Auctions', desc: 'There are no active auctions right now. Check back soon or browse upcoming auctions.' },
        'upcoming': { title: 'No Upcoming Auctions', desc: 'No auctions are scheduled yet. New auctions are added regularly.' },
        'my-bids': { title: 'No Bids Placed', desc: 'You haven\'t bid on any auctions yet. Browse active auctions to get started.' },
        'history': { title: 'No Auction History', desc: 'No completed auctions to display.' },
    };
    return (_jsxs(PageContainer, { children: [_jsxs(PageHeader, { children: [_jsxs("div", { children: [_jsxs(Title, { children: ["Auction ", _jsx("span", { children: "House" })] }), _jsx(Subtitle, { children: "Diamond auctions \u2014 bid on exceptional stones from verified dealers" })] }), _jsxs(MockModeToggle, { onClick: handleToggleMockMode, "$active": isMockMode, children: [isMockMode ? _jsx(FaToggleOn, {}) : _jsx(FaToggleOff, {}), _jsx("span", { children: isMockMode ? 'Mock Data ON' : 'Mock Data OFF' }), _jsx(FaDatabase, {})] })] }), _jsxs(ContentArea, { children: [_jsx(TabBar, { children: tabs.map(tab => (_jsxs(TabButton, { "$active": activeTab === tab.id, onClick: () => handleTabChange(tab.id), children: [tab.icon, " ", tab.label] }, tab.id))) }), _jsxs(FilterBar, { children: [_jsx(SearchInput, { type: "text", placeholder: "Search auctions by stock #, shape, carat...", value: searchTerm, onChange: handleSearch }), _jsxs(FilterSelect, { value: typeFilter, onChange: e => setTypeFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Types" }), _jsx("option", { value: "supervised", children: "Live Supervised" }), _jsx("option", { value: "automated", children: "Automated" })] }), _jsx(FilterSelect, { value: shapeFilter, onChange: e => setShapeFilter(e.target.value), children: SHAPES.map(shape => (_jsx("option", { value: shape, children: shape }, shape))) }), _jsx(FilterSelect, { value: sortBy, onChange: e => setSortBy(e.target.value), children: SORT_OPTIONS.map(opt => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) }), _jsxs(RefreshButton, { onClick: refresh, title: "Refresh auctions", children: [_jsx(FaSync, {}), " Refresh"] })] }), isLoading ? (_jsx(LoadingSpinner, { children: "Loading auctions..." })) : error ? (_jsxs(ErrorState, { children: [_jsx("h3", { children: "Something went wrong" }), _jsx("p", { children: error }), _jsx("button", { onClick: refresh, children: "Try Again" })] })) : filteredAuctions.length === 0 ? (_jsxs(EmptyState, { children: [_jsx(FaSearch, {}), _jsx("h3", { children: emptyMessages[activeTab].title }), _jsx("p", { children: emptyMessages[activeTab].desc })] })) : (_jsxs(_Fragment, { children: [_jsx(ResultsHeader, { children: _jsxs(ResultsCount, { children: ["Showing ", _jsx("strong", { children: filteredAuctions.length }), " of ", _jsx("strong", { children: total }), " auction", total !== 1 ? 's' : ''] }) }), _jsx(AuctionGrid, { children: filteredAuctions.map(auction => (_jsx(AuctionCard, { auction: auction, onClick: () => handleAuctionClick(auction.id) }, auction.id))) }), totalPages > 1 && (_jsxs(PaginationContainer, { children: [_jsx(PaginationButton, { onClick: () => changePage(page - 1), disabled: page <= 1, title: "Previous page", children: _jsx(FaChevronLeft, {}) }), pageNumbers[0] > 1 && (_jsxs(_Fragment, { children: [_jsx(PaginationButton, { onClick: () => changePage(1), children: "1" }), pageNumbers[0] > 2 && _jsx(PaginationInfo, { children: "..." })] })), pageNumbers.map(p => (_jsx(PaginationButton, { "$active": p === page, onClick: () => changePage(p), children: p }, p))), pageNumbers[pageNumbers.length - 1] < totalPages && (_jsxs(_Fragment, { children: [pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (_jsx(PaginationInfo, { children: "..." })), _jsx(PaginationButton, { onClick: () => changePage(totalPages), children: totalPages })] })), _jsx(PaginationButton, { onClick: () => changePage(page + 1), disabled: page >= totalPages, title: "Next page", children: _jsx(FaChevronRight, {}) })] }))] }))] })] }));
};
const MockModeToggle = styled.button `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.$active ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-tertiary, #f3f4f6)'};
  color: ${props => props.$active ? '#10b981' : 'var(--text-secondary, #6b7280)'};
  border: 1px solid ${props => props.$active ? '#10b981' : 'var(--border-color, #d1d5db)'};
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: ${props => props.$active ? 'rgba(16, 185, 129, 0.25)' : 'var(--bg-secondary, #e5e7eb)'};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;
export default AuctionHouse;
